import type { MouseEvent } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";

/**
 * Small pieces the Delivery tab's lanes and rows share (2026-10-26 redesign):
 * the order's number, which opens its bill, how long ago the bill opened, and
 * the «N items ⌄» toggle that shows the order's lines under it.
 */

/** «#12», a button that opens the bill when the session may open one. */
export function OrderNumber({
  seq,
  canOpen,
  testId,
  onOpen,
}: {
  seq: number;
  canOpen: boolean;
  testId: string;
  onOpen: (event: MouseEvent) => void;
}) {
  const t = useTranslate();
  if (!canOpen) {
    return (
      <span data-ui="dl-no" className="tabular-nums">
        <bdi dir="ltr">#{seq}</bdi>
      </span>
    );
  }
  return (
    <button
      type="button"
      data-ui="dl-no"
      data-testid={testId}
      aria-label={t((m) => m.dispatch.openBillAria, { sequence: seq })}
      title={t((m) => m.dispatch.openBillAria, { sequence: seq })}
      onClick={onOpen}
      className="tabular-nums"
    >
      <bdi dir="ltr">#{seq}</bdi>
    </button>
  );
}

/** «منذ 9 د», then hours — from `since` (an ISO instant) to `now` (ms). */
export function LaneAge({ since, now }: { since: string | null | undefined; now: number }) {
  const t = useTranslate();
  const at = since ? Date.parse(since) : NaN;
  if (Number.isNaN(at)) return null;
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  return (
    <span data-ui="dl-age" className="whitespace-nowrap tabular-nums">
      {minutes >= 60
        ? t((m) => m.dispatch.laneAgeHours, { count: Math.floor(minutes / 60) })
        : t((m) => m.dispatch.laneAge, { count: minutes })}
    </span>
  );
}

/**
 * «3 أصناف ⌄» — shows or hides the order's lines (`WaitingOrderLines`). Never
 * selects or opens what it sits in: the click stops here.
 */
export function ItemsToggle({
  seq,
  count,
  open,
  onToggle,
  testId,
}: {
  seq: number;
  count: number;
  open: boolean;
  onToggle: () => void;
  testId: string;
}) {
  const t = useTranslate();
  return (
    <button
      type="button"
      data-ui="dl-items"
      data-open={open ? "" : undefined}
      aria-expanded={open}
      aria-label={t((m) => m.dispatch.waitingShowItems, { sequence: seq })}
      title={t((m) => m.dispatch.waitingShowItems, { sequence: seq })}
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      onKeyDown={(event) => event.stopPropagation()}
      className="flex items-center"
    >
      <span>
        {t(
          countedKey(count, {
            one: (m) => m.dispatch.waitingItemsOne,
            two: (m) => m.dispatch.waitingItemsTwo,
            few: (m) => m.dispatch.waitingItemsFew,
            many: (m) => m.dispatch.waitingItems,
          }),
          { count },
        )}
      </span>
      {open ? (
        <ChevronUp aria-hidden="true" size={14} strokeWidth={2} />
      ) : (
        <ChevronDown aria-hidden="true" size={14} strokeWidth={2} />
      )}
    </button>
  );
}

/** One toggle state per list: which orders show their lines. */
export function toggleIn(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}
